import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useConfigContext } from './ConfigContext';
import { useLanguageContextAPI } from './LanguageContext';
import { getApiBaseUrl } from '../utils/apiBase';
import { MenuGroupItem } from '../types';

const AllScreensDetailsContext = createContext<{ menuGroup: MenuGroupItem[] }>({
  menuGroup: [],
});

export default function AllScreensDetailsContextProvider({ children }) {
  const [allScreenDetails, setAllScreenDetails] = useState([]);
  const [menuGroup, setMenuGroup] = useState<MenuGroupItem[]>([]);
  const { configSchema } = useConfigContext();
  const { language } = useLanguageContextAPI();
  const apiBase = getApiBaseUrl();

  useEffect(() => {
    async function fetchAllScreens() {
      try {
        const response = await (await fetch(`${apiBase}/api/menuStructure`)).json();
        setAllScreenDetails(response[`${configSchema}_mbd_menu_structure`]);
      } catch (error) {
        return error;
      }
    }
    fetchAllScreens();
  }, []);

  const createChildMenu = (item, language) => {
    return {
      id: item.id,
      appNameId: item.mbd_menu.menu_internal_name,
      screenName: item.mbd_menu[`menu_display_name_${language}`],
      sortOrder: item.sortOrder,
    };
  };

  const buildSubChildMenu = (structure, language) => {
    if (!structure?.length) return [];

    return structure
      .map((item) => createChildMenu(item, language))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  };

  const createChildMenuNode = (child, parentMenuName, language) => {
    return {
      id: child.id,
      appName: parentMenuName,
      appNameId: child.mbd_menu.menu_internal_name,
      screenName: child.mbd_menu[`menu_display_name_${language}`],
      sortOrder: child.sortOrder,
      subChildMenu: buildSubChildMenu(child.mbdMenuStructure, language),
      description:
        child.mbd_menu.mbd_screens?.[0]?.[`description_${language}`] ||
        child.mbd_menu[`menu_display_name_${language}`],
    };
  };

  const buildParentMenu = (item, language) => {
    const parentMenuName = item.mbdParentMenu[`parentMenuDisplayName${language}`];

    const subMenu = item.mbdMenuStructure.map((child) =>
      createChildMenuNode(child, parentMenuName, language),
    );

    subMenu.sort((a, b) => a.sortOrder - b.sortOrder);

    return {
      id: item.id,
      appName: parentMenuName,
      subMenu,
    };
  };

  useEffect(() => {
    if (!allScreenDetails?.length) return;

    const menu = allScreenDetails.map((item) => buildParentMenu(item, language));

    setMenuGroup(menu);
  }, [allScreenDetails, language]);

  const value = useMemo(() => ({ menuGroup }), [menuGroup]);

  return (
    <AllScreensDetailsContext.Provider value={value}>{children}</AllScreensDetailsContext.Provider>
  );
}

export function useAllScreensContext() {
  const context = useContext(AllScreensDetailsContext);
  if (context === undefined) {
    throw new Error('Screen Details Context must be used within a Provider');
  }
  return context;
}
